import React, { useMemo, useState } from 'react';
import HeaderLogo from 'components/common/headerLogo';
import Button from 'components/common/button';
import { isEmptyString, parseErrorMessage } from 'utils/helper';
import Spinner from 'components/spinner/Spinner';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { NullableCommunique } from 'types/types';
import styles from 'components/podiatry/page.module.scss';
import clsx from 'clsx';
import Confirmation from 'components/appointmentConfirmation/confirmation';
import { useSetAppointmentConfirmation } from 'hooks/useApi';
import { getPatientAppointmentChangeBlockedMessage } from 'utils/patientAppointmentChangeError';

type StatusType = 'Confirmed' | 'Rescheduled' | 'Cancelled';

export type AppointmentOptions = {
  title: string;
  value: string;
  type: StatusType
}

const appointmentStatus: readonly AppointmentOptions[] = [
  {
    title: 'Confirm Appointment',
    value: 'Confirmed',
    type: 'Confirmed',
  },
  {
    title: 'Have the office call me to reschedule',
    value: 'Call To Reschedule',
    type: 'Rescheduled',
  },
  {
    title: 'Cancel Appointment',
    value: 'Cancelled',
    type: 'Cancelled',
  },
];

function AppointmentConfirmation() {
  const [selectedStatus, setSelectedStatus] = useState<AppointmentOptions>();
  const { mutateAsync: setAppointmentConfirmation } = useSetAppointmentConfirmation();
  const [isConfirm, setIsConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;

  const onSubmit = async () => {
    setErrorMessage('');
    if (selectedStatus?.type === 'Cancelled') {
      setIsConfirm(true);
    } else {
      const payload = {
        appointmentId: communiqueData?.appointmentId ?? 0,
        confirmationStatus: selectedStatus?.value ?? '',
      };
      setIsLoading(true);
      try {
        await setAppointmentConfirmation(payload);
        setIsConfirm(true);
      } catch (error) {
        setErrorMessage(
          getPatientAppointmentChangeBlockedMessage(error) ?? parseErrorMessage(error),
        );
        setSelectedStatus(undefined);
        setIsConfirm(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onAppointmentChangeError = (error: unknown) => {
    setErrorMessage(
      getPatientAppointmentChangeBlockedMessage(error) ?? parseErrorMessage(error),
    );
    setSelectedStatus(undefined);
    setIsConfirm(false);
    setIsLoading(false);
  };

  const isCancel = useMemo(() => selectedStatus?.type === 'Cancelled' && isConfirm, [isConfirm, selectedStatus?.type]);

  return (
    <div className="app-page-container">
      <div className="px-3">
        <HeaderLogo />
        {!isConfirm ? (
          <div className="mt-8">
            <h2 className={clsx(styles.titleText, '!mb-4')}>Confirm your appointment</h2>
            {!isEmptyString(errorMessage) && <p className="error-message">{errorMessage}</p>}
            <div>
              {
              appointmentStatus.map((option: AppointmentOptions) => {
                const isSelected = option.type === selectedStatus?.type;
                const classes = clsx('option', { 'selected-option': isSelected });
                return (
                  <p
                    className={classes}
                    key={option.type}
                    onClick={() => { setSelectedStatus(option); }}
                    onKeyUp={() => {}}
                  >
                    {option.title}
                  </p>
                );
              })
            }
            </div>
            {!isLoading ? (
              <div className="my-4">
                <Button
                  onClick={onSubmit}
                  disabled={isEmptyString(selectedStatus)}
                >
                  {
                  selectedStatus?.type === 'Cancelled'
                    ? 'Continue'
                    : 'Confirm'
                }
                </Button>
              </div>
            ) : (
              <div className="flex flex-auto justify-center items-center text-xs">
                <Spinner />
                Loading...
              </div>
            )}
          </div>
        ) : (
          <Confirmation
            isCancel={isCancel}
            selectedStatus={selectedStatus}
            doNotCancel={(value) => { setIsConfirm(value); }}
            onAppointmentChangeError={onAppointmentChangeError}
          />
        )}
      </div>
    </div>
  );
}

export default AppointmentConfirmation;
